import FacilityActions from '../actions/FacilityActions';
import KnowledgeBookActions from '../actions/KnowledgeBookActions';
import OntologyActions from '../actions/OntologyActions';
import ViewerActions from '../actions/ViewerActions';
import FacilityStore from '../stores/FacilityStore';
import ViewerStore from '../stores/ViewerStore';
import KnowledgeBookStore from '../stores/KnowledgeBookStore';
import { getPanoTagNavigationActions, getTagNavigationReturnView, type AssetTagNavigationAction } from './assetTagNavigation';

declare const InovxLoader: any;

export const getPanoTagNavigationMenuItems = (tag: IAssetTag | null | undefined) =>
    getPanoTagNavigationActions(tag, FacilityStore.getRootOntologyContentTypes() ?? []).map(id => ({
        id,
        label: id === 'openTagScan'
            ? InovxLoader.t('facility:action.openTagScanPosition', {
                name: tag.View?.Name ?? FacilityStore.getSelectedFacility()?.Views?.find(view => view.ID === tag.View360ID)?.Name ?? '',
            })
            : InovxLoader.t('facility:action.flyToTagIn3DModelViewer'),
        iconClass: id === 'openTagScan' ? 'view-focus-icon' : 'new-view-focus-icon',
    }));

export const navigateAssetTag = async (action: AssetTagNavigationAction, tag: IAssetTag) => {
    if (action === 'openTagScan') {
        const view = FacilityStore.getSelectedFacility()?.Views?.find(item => item.ID === tag.View360ID);
        if (!view) return;
        if (!ViewerStore.isPanoViewerVisible() || ViewerStore.getSelectedViewImage()?.ID !== view.ID) {
            if (ViewerActions.checkForKnowledgeViewChanges(() => navigateAssetTag(action, tag))) return;
            const selectedAssetSet = ViewerStore.getSelectedAssetSet();
            const knowledgeView = ViewerStore.getSelectedKnowledgeView();
            if (knowledgeView && knowledgeView.KnowledgeBookID === KnowledgeBookStore.getSelectedKnowledgeBook()?.ID) {
                ViewerActions.closeKnowledgeView(true);
            }
            ViewerActions.showPanoViewerImage(view, tag);
            // Opening a new scan resets the viewer content, including the selected
            // Asset Set. Restore it so Manage Asset Sets can keep its result pane.
            if (selectedAssetSet) await ViewerActions.selectAssetSet(selectedAssetSet);
        }
    } else {
        if (!tag.Target3D) return;
        if (!ViewerStore.is3DViewerVisible()) {
            if (ViewerActions.checkForKnowledgeViewChanges(() => navigateAssetTag(action, tag))) return;
            const selectedAssetSet = ViewerStore.getSelectedAssetSet();
            const returnView = getTagNavigationReturnView(
                KnowledgeBookStore.getSelectedKnowledgeBook()?.ID,
                ViewerStore.getSavedKnowledgeView(),
                KnowledgeBookStore.getAllKnowledgeView() ?? [],
            );
            if (returnView) {
                const ontology = FacilityStore.getSelectedFacility()?.OntologyParts?.find(part => part.ID === returnView.OntologyID);
                if (ontology) OntologyActions.selectOntology(ontology);
                // This loads the 3D data cleared by Pano and restores the selected KV,
                // so UrlSyncManager restores kvId from the authoritative viewer state.
                await FacilityActions.openKnowledgeViewInViewer(returnView);
                KnowledgeBookActions.setKnowledgeBookViewingMode('manageKV');
            } else {
                const ontology = FacilityStore.getRootOntologyPart();
                const measurements = ViewerStore.getMeasurements();
                OntologyActions.selectOntology(ontology);
                // Fly opens the model viewer; a range threshold would hide Manage Asset Sets.
                await FacilityActions.openLODViewer(ontology.ID, ['Point Cloud Data', 'CAD Data', 'Mesh Data']);
                ViewerActions.updateMeasurements(measurements);
            }
            if (selectedAssetSet) await ViewerActions.selectAssetSet(selectedAssetSet);
            ViewerActions.setContentLayers({ ...ViewerStore.getContentLayers(), pointCloud: true, assetTags: true });
        } else if (ViewerStore.getRangePosition()) {
            ViewerActions.setRangePosition(tag.Target3D);
        }
    }
    ViewerActions.selectAnnotation(tag.ID);
    ViewerActions.focusAnnotation({ ...tag });
};
